import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, isApiError } from "@/lib/api";
import {
  authMeResponseSchema,
  type AuthMeResponse,
  type AuthUser,
  type IdsTenant,
} from "./schema";

export interface Company {
  id: string;
  slug: string;
  name: string;
  companyDomain?: string;
  enabled: boolean;
  /**
   * Is the current user an IDS admin of this company? Driven by the
   * caller's `adminDomains` claim — see /api/auth/companies. Non-admin
   * users only see read/view actions; admin gates Add/Edit/Delete and
   * the report designer.
   */
  isAdmin: boolean;
  /**
   * Does this tenant currently hold a valid signed license? When
   * LICENSING_ENABLED=false on the server, every company has this
   * field set to true. When enabled, the $companySlug layout
   * redirects to /<slug>/license whenever this is false.
   */
  licensed: boolean;
}

/**
 * The companies (BeProduct tenants) the current user can switch to —
 * filtered server-side to the user's IDS tenant memberships. Cached for
 * 5 minutes — long enough to avoid extra round-trips while a user
 * navigates between routes; short enough that adding a tenant in
 * Settings shows up after a soft refresh.
 *
 * Used by:
 *   - the $companySlug pathless layout to resolve slug → id, reject
 *     unknown slugs, and expose `isAdmin` to nested routes;
 *   - the /  redirect to pick a default landing target;
 *   - the user-menu Companies switcher.
 */
export const companiesQueryOptions = queryOptions({
  queryKey: ["auth", "companies"],
  staleTime: 5 * 60 * 1000,
  retry: false,
  queryFn: () =>
    api<{ companies: Company[]; licensingEnabled: boolean }>(
      "/api/auth/companies",
    ),
});

/**
 * Auth query — single source of truth for "who's logged in" + the
 * IDS-derived admin grants. Cached forever (`staleTime: Infinity`) and
 * only invalidated on login / logout, so route-guard `beforeLoad`
 * checks don't re-fetch on every navigation.
 *
 * Returns `null` for an anonymous visitor (401 from /api/auth/me),
 * which is what route guards distinguish on. Anything else throws.
 */
export const authQueryOptions = queryOptions<AuthMeResponse | null>({
  queryKey: ["auth", "me"],
  staleTime: Infinity,
  retry: false,
  queryFn: async () => {
    try {
      const raw = await api("/api/auth/me");
      return authMeResponseSchema.parse(raw);
    } catch (e) {
      if (isApiError(e) && e.status === 401) return null;
      throw e;
    }
  },
});

export function useAuth(): {
  user: AuthUser | null | undefined;
  adminDomains: string[];
  tenantDomains: string[];
  idsTenants: IdsTenant[];
  isLoading: boolean;
  logout: () => Promise<void>;
} {
  const query = useQuery(authQueryOptions);
  const queryClient = useQueryClient();
  return {
    user: query.data?.user,
    adminDomains: query.data?.adminDomains ?? [],
    tenantDomains: query.data?.tenantDomains ?? [],
    idsTenants: query.data?.idsTenants ?? [],
    isLoading: query.isLoading,
    logout: async () => {
      await api("/api/auth/logout", { method: "POST" });
      queryClient.setQueryData(authQueryOptions.queryKey, null);
      window.location.href = "/login";
    },
  };
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      queryClient.setQueryData(authQueryOptions.queryKey, null);
      window.location.href = "/login";
    },
  });
}
